import React from "react";
import { AuthProvider, useAuthUser } from "../auth-provider";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import Container from "@material-ui/core/Container";
import {createMuiTheme, ThemeProvider} from '@material-ui/core/styles'
import PerfectScrollbar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css';
import { AnimatedSwitch} from "../index";
import {SnackbarProvider} from "notistack";
import { StoreProvider } from "../../store/store/store";
import initialState from "../../store/store/initial-state";
import reducers from "../../store/reducers"
import {loadStripe} from "@stripe/stripe-js";
import getStripeKey from "../../utils/stripe";
import {Elements} from "@stripe/react-stripe-js"
import {
    LoginPage,
    SignUpPage,
    HomePage,
    ServicePage,
    AddServicePage,
    AccountPage,
    CallLogPage,
    SupportPage,
    PurchasesPage,
    CloseAccountPage,
    PasswordPage,
    AddCardPage,
    RechargePage,
    RechargeServicePage,
    TransferPage,
    TransferServicePage,
    ReactivatePage,
} from "../../routes";

const stripePromise = loadStripe(getStripeKey())
const stripeOptions = {
    fonts: [
        { cssSrc: "https://fonts.googleapis.com/css2?family=Mulish:wght@300;400;500;700&display=swap"}
    ]
}

const theme = createMuiTheme({
    // Customise theme options here...
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: {
            xs: 0,
            sm: 600,
            md: 768,
            lg: 1024,
            xl: 1280,
        }
    },
    typography: {
        fontFamily: ['Mulish','Roboto','"Helvetica Neue"','Arial','sans-serif'].join(','),
    },
    palette: {
        secondary: {
            main:  '#673ab7',
            light: '#9a67ea',
            dark:  '#320b86',
        }
    }
});

// Customise Material UI Button as a workaround for a Safari iOS bug that doesn't update the
// color of a disabled button that is later enabled.  Specifying a transition to the colour prop
// forces the button to redraw.
theme.overrides = {
    MuiButton: {
        root: {
            transition: 'color 0.01s',  // Workaround for Safari iOS bug that doesn't update
        }
    }
}

/**
 * AppSwitcher returns a root component based on the user's auth status
 * @returns {*}
 * @constructor
 */
function AppSwitcher() {
    const auth = useAuthUser()
    return auth.user ? <AuthenticatedApp /> : <UnauthenticatedApp />
}

/**
 * AuthenticatedApp is the root component for unauthenticated users
 * @returns {*}
 * @constructor
 */
function AuthenticatedApp() {
    return (
        <Container maxWidth={'md'} style={{height:'100%'}}>
            <AnimatedSwitch>
                <Route exact path="/home"                   component={HomePage} />
                <Route exact path="/account"                component={AccountPage} />
                <Route exact path="/service/:id"            component={ServicePage} />
                <Route exact path="/add-service"            component={AddServicePage} />
                <Route exact path="/calls"                  component={CallLogPage} />
                <Route exact path="/recharge"           component={RechargePage} />
                <Route exact path="/recharge/:id"           component={RechargeServicePage} />
                <Route exact path="/transfer"           component={TransferPage} />
                <Route exact path="/transfer/:id"           component={TransferServicePage} />
                <Route exact path="/support"                component={SupportPage} />
                <Route exact path="/reactivate"             component={ReactivatePage} />
            {/*    <Route exact path="/requests/:type"         component={RequestsPage} />*/}
            {/*    <Route exact path="/support/:type"          component={RequestsPage} />*/}
                <Route exact path="/account/close"          component={CloseAccountPage} />
            {/*    <Route exact path="/account/charges"        component={ChargesPage} />*/}
                <Route exact path="/account/purchases"      component={PurchasesPage} />
                <Route exact path="/account/change-password"       component={PasswordPage} />
                <Route exact path="/account/add-card"       component={AddCardPage} />
            {/*    <Route exact path="/account/:type"          component={RequestsPage} />*/}
                <Route path="/">
                    <Redirect to="/home"/>
                </Route>
            </AnimatedSwitch>
        </Container>
    )
}

/**
 * UnauthenticatedApp is the root component when the user is not logged in
 *
 * @returns {*}
 * @constructor
 */
function UnauthenticatedApp() {
    return (
        <Switch>
            <Route exact path="/login"          component={LoginPage} />
            <Route exact path="/signup"         component={SignUpPage} />
            {/*<Route exact path="/forgot-password" component={ForgotPasswordPage} />*/}
            {/*<Route exact path="/reset-password"  component={ResetPasswordPage} />*/}
            <LoginRedirect path="/" />
        </Switch>
    )
}

/**
 * LoginRedirect is a Route that redirects to the login page passing the matched location
 * to the login page as state.  If the user then logs in successfully, the login page
 * loads the passed location/URL.
 * e.g. user is not logged in but clicks a link in a text to take them to /recharge.
 * They're redirected to the login page to sign in and the login page sends them to /recharge.
 *
 * @param props
 * @returns {*}
 * @constructor
 */
function LoginRedirect(props) {
    return (
        <Route
            {...props}
            render={(props) => <Redirect to={{pathname: '/login', state: props.location}} /> }
        />
    )
}

/**
 * App is the entry point from index.js
 *
 * It uses AuthProvider to determine if the user is logged in and the
 * AppSwitcher component to display the appropriate root component.
 *
 * @returns {*}
 * @constructor
 */
function App() {
    return (
        <StoreProvider initialState={initialState} reducer={reducers}>
            <AuthProvider>
                <ThemeProvider theme={theme}>
                    <PerfectScrollbar id="psb">
                        <SnackbarProvider>
                            <Elements stripe={stripePromise} options={stripeOptions}>
                                <Router>
                                    <AppSwitcher />
                                </Router>
                            </Elements>
                        </SnackbarProvider>
                    </PerfectScrollbar>
                </ThemeProvider>
            </AuthProvider>
        </StoreProvider>
    );
}

export default App;